import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Search,
    Plus,
    ArrowUpDown,
    Briefcase,
} from "lucide-react";
import { toast } from 'react-toastify';
import { userService } from "../../../services/userService";
import amsProjectService from "../../../services/amsProjectService";
import { useAuth } from "../../../context/AuthContext";
import ProjectCard from "../../project/components/common/ProjectCard";
import ProjectFilterBar from "../../project/components/common/ProjectFilterBar";
import { validateProjectForReviewSend } from "../../project/utils/validateProjectForReview";
import { shouldShowApprovalButtons, isRequester as checkIsRequester } from "../../../utils/roleUtils";

// Modern Clean Brand Colors - Subtle & Refined
const BRAND_COLORS = {
    primary: "#339274",          // Primary Green
    primaryHover: "#29795f",     // Darker Green for hover
    primaryLight: "#F8FCFA",     // Very subtle green background
    primarySoft: "#EDF7F2",      // Soft green background
    primaryBorder: "#D1E7DD",    // Light green border
    accent: "#A3CD39",           // Lime accent
    accentLight: "#F7FAF0",      // Light lime background
    dark: "#007F66",             // Deep teal
    neutral: {
        25: "#FCFCFD",
        50: "#F9FAFB",
        100: "#F3F4F6",
        200: "#E5E7EB",
        300: "#D1D5DB",
        400: "#9CA3AF",
        500: "#6B7280",
        600: "#4B5563",
        700: "#374151",
        800: "#1F2937",
        900: "#111827"
    },
    success: "#10B981",
    successLight: "#F0FDF4",
    error: "#EF4444",
    errorLight: "#FEF2F2",
    warning: "#F59E0B",
    warningLight: "#FFFBEB",
};

interface Props {
    projects: any[];
    search: string;
    setSearch: (val: string) => void;
    projectTypeId: string;
    setProjectTypeId: (val: string) => void;
    location: string;
    setLocation: (val: string) => void;
    approvalStatus: string;
    setApprovalStatus: (val: string) => void;
    page: number;
    setPage: (val: number) => void;
    itemsPerPage: number;
    setItemsPerPage: (val: number) => void;
    totalProjects: number;
    onEdit?: (project: any) => void;
    onAdd?: () => void;
    onSelect?: (project: any) => void;
    onOpenAssetDrawer?: (project: any) => void;
    onOpenCalendar?: (project: any) => void;
    selectedProject?: any;
    onRefresh?: () => void;
    sortField: string;
    sortOrder: "asc" | "desc";
    setSortField: (field: string) => void;
    setSortOrder: (order: "asc" | "desc") => void;
    loading?: boolean;
}


export default function AmsProjectTable({
    projects,
    search,
    setSearch,
    projectTypeId,
    setProjectTypeId,
    location,
    setLocation,
    approvalStatus,
    setApprovalStatus,
    page,
    setPage,
    itemsPerPage,
    setItemsPerPage,
    totalProjects,
    onEdit,
    onAdd,
    onSelect,
    onOpenAssetDrawer,
    onOpenCalendar,
    onRefresh,
    sortField,
    sortOrder,
    setSortField,
    setSortOrder,
    loading = false,
}: Props) {
    const navigate = useNavigate();
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [sortMenuOpen, setSortMenuOpen] = useState<string | null>(null);
    const [userCache, setUserCache] = useState<Record<string, string>>({});

    // Get current user role
    const { user } = useAuth();
    // Check if user is a requester for AMS department (for showing approval buttons)
    const showApprovalButtons = shouldShowApprovalButtons('AMS');
    const isRequesterUser = checkIsRequester('AMS'); // Use utility function to check if user is requester

    // Sorting + pagination are applied server-side; render the page exactly as
    // received so the selected sort field/order is respected.
    const sortedProjects = useMemo(
        () => (Array.isArray(projects) ? projects : []),
        [projects]
    );

    const handleViewProject = (project: any) => {
        const id = project._id || project.id;
        if (id) navigate(`/projects/view/${id}`);
    };

    const displayValidationErrors = (errors: string[]) => {
        toast.error(
            <div>
                <div className="font-bold mb-2">Cannot send for review:</div>
                <ul className="list-disc pl-4 space-y-1">
                    {errors.map((error, index) => (
                        <li key={index} className="text-sm">{error}</li>
                    ))}
                </ul>
            </div>,
            { autoClose: 8000 }
        );
    };

    const validateProjectBeforeSubmission = async (projectId: string, projectSnapshot: any) => {
        return validateProjectForReviewSend({
            projectId,
            project: projectSnapshot as Record<string, unknown>
        });
    };

    const handleSubmitForApproval = async (project: any) => {
        try {
            const projectId = project._id || project.id;
            if (!projectId) {
                toast.error('Project ID is missing. Please refresh and try again.');
                return;
            }

            const validation = await validateProjectBeforeSubmission(projectId, project);
            if (!validation.valid) {
                displayValidationErrors(validation.errors);
                return;
            }

            await amsProjectService.submitProjectForApproval(projectId, {
                comments: 'Project ready for approval'
            });

            toast.success('✅ Project submitted for approval successfully!');
            onRefresh?.();
        } catch (error: any) {
            toast.error(error.message || 'Failed to submit project');
            console.error(error);
        }
    };

    const getUserName = async (userId: string): Promise<string> => {
        if (userCache[userId]) {
            return userCache[userId];
        }

        try {
            const user = await userService.getUserById(userId);
            const userName = user.name || user.email || 'Unknown User';
            setUserCache(prev => ({ ...prev, [userId]: userName }));
            return userName;
        } catch (error) {
            console.error('Failed to fetch user:', error);
            return 'Unknown User';
        }
    };

    const handleSortOption = (field: string, order: "asc" | "desc" | "clear") => {
        if (order === "clear") {
            setSortField("createdAt");
            setSortOrder("desc");
        } else {
            setSortField(field);
            setSortOrder(order);
        }
        setSortMenuOpen(null);
        setPage(1);
    };

    useEffect(() => {
        const fetchUserNames = async () => {
            const createdByIds = projects
                .map(project => project.createdBy)
                .filter(createdBy => typeof createdBy === 'string' && !userCache[createdBy]);

            const userIds = [...new Set(createdByIds)];

            for (const userId of userIds) {
                await getUserName(userId);
            }
        };

        if (projects.length > 0) {
            fetchUserNames();
        }
    }, [projects, userCache]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "k") {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.sort-dropdown-container')) {
                setSortMenuOpen(null);
            }
        };

        if (sortMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [sortMenuOpen]);

    const totalPages = Math.ceil(totalProjects / itemsPerPage);
    const startItem = (page - 1) * itemsPerPage + 1;
    const endItem = Math.min(page * itemsPerPage, totalProjects);

    const getPageNumbers = () => {
        const delta = 2;
        const range = [];
        const rangeWithDots = [];

        for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
            range.push(i);
        }

        if (page - delta > 2) {
            rangeWithDots.push(1, "...");
        } else {
            rangeWithDots.push(1);
        }

        rangeWithDots.push(...range);

        if (page + delta < totalPages - 1) {
            rangeWithDots.push("...", totalPages);
        } else if (totalPages > 1) {
            rangeWithDots.push(totalPages);
        }

        return rangeWithDots;
    };

    const getProjectId = (project: any) => project._id || project.id || project.projectId;

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-xl shadow-sm border border-gray-100"
            >
                <div className="p-4 sm:p-5 border-b border-gray-100 bg-white font-[family-name:var(--font-outfit)]">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="relative flex-1 min-w-0 max-w-md">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-4 w-4 text-gray-400" />
                                </div>
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setPage(1);
                                    }}
                                    className="block w-full pl-9 pr-20 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:bg-white focus:ring-2 focus:border-transparent focus:outline-none transition-all duration-200"
                                    style={
                                        {
                                            "--tw-ring-color": BRAND_COLORS.primary,
                                        } as React.CSSProperties
                                    }
                                    placeholder="Search by project name…"
                                />
                                <div className="absolute inset-y-0 right-0 pr-2 flex items-center gap-1">
                                    <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold text-gray-400 bg-white border border-gray-200 rounded">
                                        ⌘K
                                    </kbd>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                                <div className="relative sort-dropdown-container">
                                    <button
                                        onClick={() =>
                                            setSortMenuOpen(sortMenuOpen ? null : "general")
                                        }
                                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 transition-all duration-200"
                                        style={
                                            {
                                                "--tw-ring-color": BRAND_COLORS.primary,
                                            } as React.CSSProperties
                                        }
                                    >
                                        <ArrowUpDown className="w-4 h-4" />
                                        Sort
                                    </button>

                                    <AnimatePresence>
                                        {sortMenuOpen === "general" && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -8 }}
                                                className="absolute top-full right-0 mt-1.5 z-20 w-44 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden py-1.5"
                                            >
                                                <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-1.5">
                                                    Name
                                                </div>
                                                <button
                                                    onClick={() =>
                                                        handleSortOption("projectName", "asc")
                                                    }
                                                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg mx-1 transition ${sortField === "projectName" && sortOrder === "asc"
                                                        ? "font-semibold"
                                                        : "text-gray-700 hover:bg-gray-50"
                                                        }`}
                                                    style={
                                                        sortField === "projectName" && sortOrder === "asc"
                                                            ? {
                                                                color: BRAND_COLORS.primary,
                                                                backgroundColor: BRAND_COLORS.primaryLight,
                                                            }
                                                            : {}
                                                    }
                                                >
                                                    A → Z
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        handleSortOption("projectName", "desc")
                                                    }
                                                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg mx-1 transition ${sortField === "projectName" && sortOrder === "desc"
                                                        ? "font-semibold"
                                                        : "text-gray-700 hover:bg-gray-50"
                                                        }`}
                                                    style={
                                                        sortField === "projectName" && sortOrder === "desc"
                                                            ? {
                                                                color: BRAND_COLORS.primary,
                                                                backgroundColor: BRAND_COLORS.primaryLight,
                                                            }
                                                            : {}
                                                    }
                                                >
                                                    Z → A
                                                </button>
                                                <div className="border-t border-gray-100 my-1.5" />
                                                <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-1.5">
                                                    Date
                                                </div>
                                                <button
                                                    onClick={() => handleSortOption("createdAt", "desc")}
                                                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg mx-1 transition ${sortField === "createdAt" && sortOrder === "desc"
                                                        ? "font-semibold"
                                                        : "text-gray-700 hover:bg-gray-50"
                                                        }`}
                                                    style={
                                                        sortField === "createdAt" && sortOrder === "desc"
                                                            ? {
                                                                color: BRAND_COLORS.primary,
                                                                backgroundColor: BRAND_COLORS.primaryLight,
                                                            }
                                                            : {}
                                                    }
                                                >
                                                    Newest first
                                                </button>
                                                <button
                                                    onClick={() => handleSortOption("createdAt", "asc")}
                                                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg mx-1 transition ${sortField === "createdAt" && sortOrder === "asc"
                                                        ? "font-semibold"
                                                        : "text-gray-700 hover:bg-gray-50"
                                                        }`}
                                                    style={
                                                        sortField === "createdAt" && sortOrder === "asc"
                                                            ? {
                                                                color: BRAND_COLORS.primary,
                                                                backgroundColor: BRAND_COLORS.primaryLight,
                                                            }
                                                            : {}
                                                    }
                                                >
                                                    Oldest first
                                                </button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {onAdd && (
                                    <button
                                        onClick={onAdd}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                                        style={{
                                            background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                                            "--tw-ring-color": BRAND_COLORS.primary,
                                        } as React.CSSProperties}
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Project
                                    </button>
                                )}
                            </div>
                        </div>

                        <ProjectFilterBar
                            projectTypeId={projectTypeId}
                            setProjectTypeId={setProjectTypeId}
                            location={location}
                            setLocation={setLocation}
                            approvalStatus={approvalStatus}
                            setApprovalStatus={setApprovalStatus}
                            setPage={setPage}
                            hideStatus={true}
                        />
                    </div>
                </div>

                {
                    loading && (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BRAND_COLORS.primary }}></div>
                        </div>
                    )
                }

                {
                    !loading && (
                        <div className="p-6">
                            {projects.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 text-gray-500 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                    <Briefcase className="w-12 h-12 text-gray-300 mb-3" />
                                    <p className="text-lg font-medium text-gray-900">No projects found</p>
                                    <p className="text-sm mt-1">Try adjusting your filters or add a new project.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    <AnimatePresence>
                                        {sortedProjects.map((project) => (
                                            <ProjectCard
                                                key={getProjectId(project)}
                                                project={project}
                                                creatorName={
                                                    project.createdBy && typeof project.createdBy === 'string'
                                                        ? userCache[project.createdBy]
                                                        : project.createdBy?.name
                                                }
                                                onView={handleViewProject}
                                                onEdit={onEdit}
                                                onOpenAssetDrawer={onOpenAssetDrawer || onSelect}
                                                onOpenCalendar={onOpenCalendar}
                                                onSubmitForApproval={undefined}
                                                isRequester={false}
                                                hideStatus={false}
                                                isAms={true}
                                                auditCounts={project.auditCounts}
                                            />
                                        ))}
                                    </AnimatePresence>
                                </div>
                            )}
                        </div>
                    )
                }

                {
                    totalProjects > 0 && (
                        <div className="px-6 py-4 border-t border-gray-100">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="text-sm text-gray-600">
                                        Showing <span className="font-medium">{startItem}</span> to{" "}
                                        <span className="font-medium">{endItem}</span> of{" "}
                                        <span className="font-medium">{totalProjects}</span> projects
                                    </div>
                                    <label className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <span className="hidden sm:inline">Rows:</span>
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                            className="border border-gray-200 rounded-lg px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                                        >
                                            {[5, 10, 25, 50].map((n) => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setPage(Math.max(1, page - 1))}
                                            disabled={page === 1}
                                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                        >
                                            Previous
                                        </button>

                                        <div className="hidden sm:flex items-center gap-1">
                                            {getPageNumbers().map((pageNum, idx) =>
                                                pageNum === "..." ? (
                                                    <span key={`ellipsis-${idx}`} className="px-3 py-2 text-gray-400">
                                                        ...
                                                    </span>
                                                ) : (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setPage(pageNum as number)}
                                                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${page === pageNum
                                                            ? 'text-white border-transparent'
                                                            : 'text-gray-500 bg-white border border-gray-200 hover:bg-gray-50 hover:text-gray-700'
                                                            }`}
                                                        style={page === pageNum ? { backgroundColor: BRAND_COLORS.primary } : {}}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                )
                                            )}
                                        </div>

                                        <button
                                            onClick={() => setPage(Math.min(totalPages, page + 1))}
                                            disabled={page === totalPages}
                                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }
            </motion.div>
        </>
    );
}
